import { useCallback } from 'react';

export type HapticFeedbackType = 
  | 'selection'     // Light tap (10ms) - piece selection
  | 'success'       // Gentle confirmation (30ms) - successful move
  | 'capture'       // Strong pulse (100ms) - taking opponent pieces
  | 'check'         // Triple pattern (200ms + 100ms + 200ms) - check/checkmate
  | 'invalid'       // Double tap (50ms + 50ms) - illegal moves
  | 'victory'       // Celebration pattern (100ms + 50ms + 100ms + 50ms + 150ms)
  | 'defeat';       // Sad pattern (300ms)

export function useHapticFeedback() {
  const vibrate = useCallback((pattern: number | number[]) => {
    // Check if the Vibration API is supported
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
      } catch (error) {
        console.warn('Haptic feedback failed:', error);
      }
    }
  }, []);

  const triggerHaptic = useCallback((type: HapticFeedbackType) => {
    // Check user preferences for reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    switch (type) {
      case 'selection':
        vibrate(10);
        break;
      case 'success':
        vibrate(30);
        break;
      case 'capture':
        vibrate(100);
        break;
      case 'check':
        vibrate([200, 50, 100, 50, 200]);
        break;
      case 'invalid':
        vibrate([50, 50, 50]);
        break;
      case 'victory':
        vibrate([100, 50, 100, 50, 150]);
        break;
      case 'defeat':
        vibrate(300);
        break;
      default:
        break;
    }
  }, [vibrate]);

  const isHapticSupported = 'vibrate' in navigator;

  return {
    triggerHaptic,
    isHapticSupported
  };
}